import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import type {
  MedicationScan,
  MedicationScanFilters,
} from '../types/medication.types';

export const MEDICATIONS_QUERY_KEY = 'medications';

/**
 * Интерфейс для paginated ответа в формате useQuery
 */
export interface PaginatedMedicationScans {
  scans: MedicationScan[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Хук для получения списка сканирований препаратов с фильтрацией и пагинацией
 *
 * @param filters - Фильтры поиска (search, employee_id, patient_iin, from, to, page, page_size)
 * @returns useQuery результат с данными, loading, error состояниями
 *
 * @example
 * const { data, isLoading, error, refetch } = useMedicationScans({
 *   search: '04870004560161',
 *   employee_id: 'uuid',
 *   page: 1,
 *   page_size: 20,
 * });
 */
export const useMedicationScans = (filters: MedicationScanFilters = {}) => {
  return useQuery({
    queryKey: [MEDICATIONS_QUERY_KEY, 'scans', filters],
    queryFn: async (): Promise<PaginatedMedicationScans> => {
      const response: any = await apiClient.getMedicationScans(filters);

      // Приводим ответ API к нужному формату
      // Backend возвращает { scans: [], pagination: {...} }
      return {
        scans: response.scans || [],
        total: response.pagination?.total_items || 0,
        page: response.pagination?.page || 1,
        pageSize: response.pagination?.page_size || 20,
        totalPages: response.pagination?.total_pages || 1,
        hasNext: response.pagination?.has_next || false,
        hasPrevious: response.pagination?.has_previous || false,
      };
    },
    staleTime: 30 * 1000, // 30 seconds - кеш данных
    retry: 3, // 3 попытки при ошибке
  });
};

/**
 * Хук для получения одного сканирования препарата по ID (опционально, если потребуется)
 *
 * @param id - ID сканирования
 * @returns useQuery результат с данными сканирования
 */
export const useMedicationScan = (id: string) => {
  return useQuery({
    queryKey: [MEDICATIONS_QUERY_KEY, 'scan', id],
    queryFn: async (): Promise<MedicationScan | null> => {
      // Получаем список с фильтром по ID (или можно создать отдельный endpoint)
      const response: any = await apiClient.getMedicationScans({
        page: 1,
        page_size: 1
      });

      const scan = response.scans?.find((s: MedicationScan) => s.id === id);
      return scan || null;
    },
    enabled: !!id, // Запрос выполняется только если ID передан
    staleTime: 60 * 1000, // 60 seconds для детальной страницы
  });
};

/**
 * Хук для повторной отправки сканирования в 1С
 *
 * @returns useMutation результат для retry операции
 *
 * @example
 * const retryMutation = useRetryMedicationScan();
 * retryMutation.mutate(scanId, {
 *   onSuccess: () => console.log('Retry initiated'),
 *   onError: (error) => console.error(error),
 * });
 */
export const useRetryMedicationScan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scanId: string) => {
      return apiClient.retryMedicationScanSync(scanId);
    },
    onSuccess: () => {
      // Инвалидируем все queries связанные с medications для обновления UI
      queryClient.invalidateQueries({ queryKey: [MEDICATIONS_QUERY_KEY] });
    },
  });
};

/**
 * Результат batch retry операции
 */
export interface BatchRetryResult {
  total: number;
  retried: number;
  message: string;
}

/**
 * Хук для повторной отправки всех неотправленных записей (pending/failed) в 1С
 *
 * @returns useMutation результат для batch retry операции
 *
 * @example
 * const retryPendingMutation = useRetryPendingMedicationScans();
 * retryPendingMutation.mutate(undefined, {
 *   onSuccess: (result) => console.log(`Отправлено ${result.retried} записей`),
 *   onError: (error) => console.error(error),
 * });
 */
export const useRetryPendingMedicationScans = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<BatchRetryResult> => {
      const response = await apiClient.retryPendingMedicationScans();
      return response as unknown as BatchRetryResult;
    },
    onSuccess: () => {
      // Инвалидируем все queries связанные с medications для обновления UI
      queryClient.invalidateQueries({ queryKey: [MEDICATIONS_QUERY_KEY] });
    },
  });
};
